import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation dictionary
const translations = {
  en: {
    // Navigation
    HOME: 'HOME',
    ABOUT: 'ABOUT',
    ABOUT_US: 'ABOUT US',
    OUR_TEAM: 'OUR TEAM',
    HOW_IT_WORKS: 'HOW IT WORKS',
    PROPERTIES: 'PROPERTIES',
    ALL_PROPERTIES: 'ALL PROPERTIES',
    GALLERY: 'GALLERY',
    FAQ: 'FAQ',
    BLOG: 'BLOG',
    CONTACT: 'CONTACT',
    REQUEST_A_VIEWING: 'REQUEST A VIEWING',
    
    // Common
    LANGUAGE: 'Language',
    CURRENCY: 'Currency',
    ENGLISH: 'English',
    LUGANDA: 'Luganda',
    US_DOLLAR: 'US Dollar',
    EURO: 'Euro',
    POUND_STERLING: 'Pound Sterling',
    UGANDAN_SHILLING: 'Ugandan Shilling',
    
    // Property Details
    BEDROOMS: 'Bedrooms',
    BATHROOMS: 'Bathrooms',
    AREA: 'Area (sqft)',
    PARKING: 'Parking',
    DESCRIPTION: 'Description',
    AMENITIES: 'Amenities & Features',
    PRICE: 'Price',
    STATUS: 'Status',
    CATEGORY: 'Category',
    LOCATION: 'Location',
    
    // Forms
    FULL_NAME: 'Full Name',
    EMAIL: 'Email',
    PHONE: 'Phone',
    MESSAGE: 'Message',
    SEND_MESSAGE: 'Send Message',
    SCHEDULE_VIEWING: 'Schedule a showing?',
    
    // Actions
    VIEW_ON_MAPS: 'View on Google Maps',
    CALL: 'Call',
    EMAIL_LINK: 'Email',
  },
  lg: {
    // Navigation
    HOME: 'ETTEGEEZA',
    ABOUT: 'EBITUKWATA KO',
    ABOUT_US: 'EBITUKWATA KWEETHU',
    OUR_TEAM: 'EKIBIINA KYE KKUMI',
    HOW_IT_WORKS: 'ENGERI EZIKOLA',
    PROPERTIES: 'EBINTU',
    ALL_PROPERTIES: 'ENNIMIRO ZONNA',
    GALLERY: 'EKITABO KY\'EBIFO',
    FAQ: 'EBIBUUZO',
    BLOG: 'BLOGU',
    CONTACT: 'OKUKUUMA',
    REQUEST_A_VIEWING: 'OKUTEGEEZA',
    
    // Common
    LANGUAGE: 'Olulimi',
    CURRENCY: 'Sente',
    ENGLISH: 'Kingereza',
    LUGANDA: 'Luganda',
    US_DOLLAR: 'Doola ya America',
    EURO: 'Yuro',
    POUND_STERLING: 'Paundi ya Bungereza',
    UGANDAN_SHILLING: 'Shiringi ya Uganda',
    
    // Property Details
    BEDROOMS: 'Bulamu',
    BATHROOMS: 'Bafu',
    AREA: 'Ekigazi (sqft)',
    PARKING: 'Ekigazi kya Motorokali',
    DESCRIPTION: 'Okukitegeeza',
    AMENITIES: 'Ebintu Ebikwata',
    PRICE: 'Omuwendo',
    STATUS: 'Sitiletyo',
    CATEGORY: 'Ekinnya',
    LOCATION: 'Ekinowooza',
    
    // Forms
    FULL_NAME: 'Linnya Lyonna',
    EMAIL: 'Imeyili',
    PHONE: 'Simu',
    MESSAGE: 'Obubaka',
    SEND_MESSAGE: 'Tumira Obubaka',
    SCHEDULE_VIEWING: 'Yiga okukubaako omusolo?',
    
    // Actions
    VIEW_ON_MAPS: 'Laba mu Maps',
    CALL: 'Piira',
    EMAIL_LINK: 'Imeyili',
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to 'en'
    if (typeof window !== 'undefined') {
      return localStorage.getItem('language') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    // Save language preference to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', language);
    }
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
  };

  const value = {
    language,
    setLanguage: changeLanguage,
    t,
    availableLanguages: [
      { code: 'en', name: 'English', flag: '🇬🇧' },
      { code: 'lg', name: 'Luganda', flag: '🇺🇬' },
    ],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
